import { BILLING_PLANS, TRIAL_DAYS, type PlanLimits } from '@order-bot/shared-types';

import { FAQ_BY_LANGUAGE } from '@/features/help/faqTranslations';

import { builder, faq, hero, steps } from './content';

/**
 * Everything a distributor reads on the landing page, in Hindi and English. Hindi is the default:
 * most distributors we pitch read it first. "Under the hood" stays English (it's for developers).
 *
 * The FAQ is the one list shared with the console's Help page (content.ts / faqTranslations.ts).
 */
export type LandingLang = 'hi' | 'en';
export const LANDING_LANGS: readonly LandingLang[] = ['hi', 'en'];
export const DEFAULT_LANDING_LANG: LandingLang = 'hi';

/** A plan as a pricing card shows it: rupees a month and its limits. */
export interface LandingPlan extends PlanLimits {
  price: number;
}

const landingPlan = (key: keyof typeof BILLING_PLANS): LandingPlan => ({
  price: BILLING_PLANS[key].price,
  ...BILLING_PLANS[key].limits,
});

/**
 * The paid plans, in card order. Prices and limits come from the billing table the API
 * enforces (shared-types billing.ts), so this page can never promise other numbers.
 */
export const PLANS = (['starter', 'growth', 'pro'] as const).map((key) => ({
  key,
  ...landingPlan(key),
}));
/** The free pilot's limits, from the same table. */
export const PILOT_PLAN = landingPlan('pilot');
export const PILOT_DAYS = TRIAL_DAYS;
export const SETUP_FEE = 2999;

export const rupees = (n: number): string => `₹${n.toLocaleString('en-IN')}`;

type PlanKey = (typeof PLANS)[number]['key'];

export interface LandingCopy {
  htmlLang: string;
  /** The toggle's text: the name of the *other* language, in that language. */
  switchTo: string;
  switchToLabel: string;
  nav: {
    /** The header's section links, for screen readers. */
    label: string;
    why: string;
    how: string;
    pricing: string;
    faq: string;
    about: string;
    login: string;
  };
  /** The header's menu on a phone. */
  menu: { open: string; close: string; label: string };
  hero: {
    eyebrow: string;
    title: string;
    lead: string;
    note: string;
    /** Under the illustration: it's sample data, not a customer's order. */
    sample: string;
  };
  cta: { demo: string; signup: string; login: string };
  who: { title: string; points: readonly string[] };
  why: { title: string; intro: string; items: ReadonlyArray<{ title: string; body: string }> };
  how: {
    title: string;
    intro: string;
    stepLabel: string;
    steps: ReadonlyArray<{ title: string; body: string; note?: string }>;
  };
  pricing: {
    title: string;
    intro: string;
    perMonth: string;
    ordersUpTo: (n: number) => string;
    staffUpTo: (n: number) => string;
    retailersUpTo: (n: number) => string;
    pilot: { name: string; price: string; points: readonly string[] };
    /** The badge on the pilot card. */
    startHere: string;
    /** The paid plans' button: every plan starts with the pilot. */
    planCta: string;
    plans: Record<PlanKey, { name: string; points: readonly string[] }>;
    notes: readonly string[];
  };
  faq: { title: string; intro: string; items: ReadonlyArray<{ q: string; a: string }> };
  about: { title: string; body: readonly string[]; builderTitle: string; blurb: string };
  /** The developer section, folded away near the footer. Its content stays in English. */
  hood: { summary: string };
  /** The last section before the footer. */
  closing: { title: string; body: string; whatsapp: string; whatsappText: string };
  footer: {
    tagline: string;
    productTitle: string;
    contactTitle: string;
    accountTitle: string;
    demo: string;
    email: string;
    whatsapp: string;
    signup: string;
    staffLogin: string;
    privacy: string;
    terms: string;
    developers: string;
  };
}

const en: LandingCopy = {
  htmlLang: 'en',
  switchTo: 'हिंदी',
  switchToLabel: 'हिंदी में पढ़ें',
  nav: {
    label: 'Page sections',
    why: 'Why',
    how: 'How it works',
    pricing: 'Pricing',
    faq: 'FAQ',
    about: 'About',
    login: 'Login',
  },
  menu: { open: 'Open menu', close: 'Close menu', label: 'Menu' },
  hero: {
    ...hero,
    note: 'Opens your own sandbox of sample orders, no signup. Nothing is sent to WhatsApp or Tally.',
    sample: 'Illustration with sample data',
  },
  cta: { demo: 'Try live demo', signup: 'Start free pilot', login: 'Login' },
  who: {
    title: 'Made for you if',
    points: [
      'You are an FMCG or wholesale distributor and keep your books in TallyPrime',
      'Your retailers already order on WhatsApp: voice notes or text',
      'Your staff type those orders into Tally by hand, and mistakes or delays cost you',
    ],
  },
  why: {
    title: 'Why distributors use Order Bot',
    intro: 'Less typing for your staff, nothing new for your retailers, and Tally stays as it is.',
    items: [
      {
        title: 'No more re-typing',
        body: 'Voice notes and messages become a ready Sales Order. Staff only check it and press Approve.',
      },
      {
        title: 'Retailers change nothing',
        body: 'They keep ordering on WhatsApp the way they do today. No app to install, no login.',
      },
      {
        title: 'Your Tally stays your Tally',
        body: 'Items, units, pack sizes and rates come from Tally. Approved orders go straight in as Sales Orders.',
      },
      {
        title: 'Fewer wrong items',
        body: '“2 peti” becomes the right number of pieces from Tally’s pack size. Anything unclear is flagged for a person, never guessed.',
      },
      {
        title: 'Retailers hear back',
        body: 'They get a summary to confirm, then “your order is booked” once it is in Tally. Fewer follow-up calls.',
      },
      {
        title: 'No order gets lost',
        body: 'If the Tally PC or the internet is off, approved orders wait and go in later, never twice.',
      },
    ],
  },
  how: {
    title: 'How it works',
    intro:
      'From a WhatsApp voice note to a Tally sales order, with a person approving every order.',
    stepLabel: 'Step',
    steps,
  },
  pricing: {
    title: 'Pricing',
    intro: `Start with a ${PILOT_DAYS}-day free pilot. Pay only if it saves your staff time.`,
    perMonth: '/month',
    ordersUpTo: (n) => `Up to ${n.toLocaleString('en-IN')} orders a month`,
    staffUpTo: (n) => `${n} staff logins`,
    retailersUpTo: (n) => `Up to ${n.toLocaleString('en-IN')} retailers`,
    pilot: {
      name: 'Free pilot',
      price: `${PILOT_DAYS} days free`,
      points: [
        'Your own WhatsApp and your own Tally',
        'We set it up with you and train your staff',
        'No card, no contract. Stop any day',
      ],
    },
    startHere: 'Start here',
    planCta: 'Start with the free pilot',
    plans: {
      starter: { name: 'Starter', points: ['1 Tally company'] },
      growth: { name: 'Growth', points: ['2 Tally companies', 'Priority support'] },
      pro: { name: 'Pro', points: ['5 Tally companies', 'Same-day support'] },
    },
    notes: [
      'More orders than your plan? Orders never stop when you pass the monthly limit; we contact you to move to the next plan.',
      `One-time setup ${rupees(SETUP_FEE)}, free for pilot distributors.`,
      'Pay yearly: 12 months for the price of 10.',
      'An order is one Sales Order posted to Tally; questions and rejected orders are not counted.',
    ],
  },
  faq: {
    title: 'Questions distributors ask',
    intro: 'What changes for your retailers, your staff and your Tally.',
    items: faq,
  },
  about: {
    title: 'About Order Bot',
    body: [
      'Order Bot turns the WhatsApp orders your retailers already send into Sales Orders in your Tally, with your staff approving each one.',
      'Your data is yours: we use it only to run the service, never sell or share it, and erase it when you ask.',
    ],
    builderTitle: 'Who builds it',
    blurb: builder.blurb,
  },
  hood: { summary: 'For developers: how Order Bot is built' },
  closing: {
    title: 'Try Order Bot on your own orders',
    body: `A ${PILOT_DAYS}-day free pilot with your WhatsApp number and your Tally. We set it up with you and train your staff. No card, no contract.`,
    whatsapp: 'Chat on WhatsApp',
    whatsappText: 'Hi, I would like to know more about Order Bot for my distribution business.',
  },
  footer: {
    tagline: 'WhatsApp orders into TallyPrime, with your staff approving each one.',
    productTitle: 'Order Bot',
    contactTitle: 'Contact',
    accountTitle: 'Account',
    demo: 'Live demo',
    email: 'Email',
    whatsapp: 'WhatsApp',
    signup: 'Sign up your business',
    staffLogin: 'Staff login',
    privacy: 'Privacy policy',
    terms: 'Terms of service',
    developers: 'For developers',
  },
};

const hi: LandingCopy = {
  htmlLang: 'hi',
  switchTo: 'English',
  switchToLabel: 'Read in English',
  nav: {
    label: 'पेज के हिस्से',
    why: 'क्यों',
    how: 'कैसे काम करता है',
    pricing: 'कीमत',
    faq: 'सवाल-जवाब',
    about: 'हमारे बारे में',
    login: 'लॉगिन',
  },
  menu: { open: 'मेन्यू खोलें', close: 'मेन्यू बंद करें', label: 'मेन्यू' },
  hero: {
    eyebrow: 'FMCG और होलसेल डिस्ट्रीब्यूटर के लिए',
    title: 'WhatsApp ऑर्डर से Tally तक, एक अप्रूवल में।',
    lead: 'Order Bot वॉइस नोट सुनकर हर आइटम को आपकी Tally की स्टॉक लिस्ट से मिलाता है और ऑर्डर तैयार रखता है। आपका स्टाफ अप्रूव करे, तभी वह Tally में जाता है।',
    note: 'सैंपल ऑर्डर के साथ आपका अपना डेमो खुलेगा, साइनअप नहीं चाहिए। WhatsApp या Tally पर कुछ नहीं जाएगा।',
    sample: 'सैंपल डेटा वाला उदाहरण',
  },
  cta: { demo: 'लाइव डेमो देखें', signup: 'फ्री पायलट शुरू करें', login: 'लॉगिन' },
  who: {
    title: 'यह आपके लिए है, अगर',
    points: [
      'आप FMCG या होलसेल डिस्ट्रीब्यूटर हैं और हिसाब TallyPrime में रखते हैं',
      'आपके रिटेलर पहले से WhatsApp पर ऑर्डर भेजते हैं: वॉइस नोट या मैसेज',
      'आपका स्टाफ ये ऑर्डर हाथ से Tally में चढ़ाता है, और गलती या देरी से नुकसान होता है',
    ],
  },
  why: {
    title: 'डिस्ट्रीब्यूटर Order Bot क्यों लगाते हैं',
    intro: 'स्टाफ की टाइपिंग कम, रिटेलर के लिए कुछ नया नहीं, और Tally जैसी है वैसी रहती है।',
    items: [
      {
        title: 'दोबारा टाइप करना बंद',
        body: 'वॉइस नोट और मैसेज से Sales Order तैयार हो जाता है। स्टाफ बस जाँचकर Approve दबाता है।',
      },
      {
        title: 'रिटेलर को कुछ नहीं बदलना',
        body: 'वे आज की तरह WhatsApp पर ही ऑर्डर करते रहेंगे। न कोई ऐप, न लॉगिन।',
      },
      {
        title: 'आपकी Tally, आपकी ही रहेगी',
        body: 'आइटम, यूनिट, पैक साइज़ और रेट Tally से आते हैं। अप्रूव हुआ ऑर्डर सीधे Sales Order बनकर Tally में जाता है।',
      },
      {
        title: 'गलत आइटम कम',
        body: '“2 पेटी” को Tally के पैक साइज़ से सही पीस में बदला जाता है। जो साफ़ न हो, वह स्टाफ के पास जाता है; अंदाज़ा कभी नहीं लगाया जाता।',
      },
      {
        title: 'रिटेलर को जवाब मिलता है',
        body: 'कन्फर्म करने के लिए ऑर्डर का सार, और Tally में चढ़ते ही “आपका ऑर्डर बुक हो गया”। फ़ॉलो-अप कॉल कम।',
      },
      {
        title: 'कोई ऑर्डर नहीं खोता',
        body: 'Tally वाला PC या इंटरनेट बंद हो, तो अप्रूव हुए ऑर्डर इंतज़ार करते हैं और बाद में जाते हैं, दो बार कभी नहीं।',
      },
    ],
  },
  how: {
    title: 'कैसे काम करता है',
    intro:
      'WhatsApp के वॉइस नोट से Tally के Sales Order तक, हर ऑर्डर पर आपके स्टाफ की मंज़ूरी के साथ।',
    stepLabel: 'स्टेप',
    steps: [
      {
        title: 'WhatsApp पर ऑर्डर आता है',
        body: 'रिटेलर डिस्ट्रीब्यूटर के WhatsApp Business नंबर पर वॉइस नोट या मैसेज भेजता है, जैसे आज भेजता है।',
        note: 'हाथ से लिखी लिस्ट की फ़ोटो जल्द आ रही है।',
      },
      {
        title: 'AI पढ़ता है',
        body: 'वॉइस नोट टेक्स्ट बनता है, AI आइटम और मात्रा निकालता है, और हर आइटम को आपकी Tally की स्टॉक लिस्ट से मिलाता है।',
      },
      {
        title: 'स्टाफ जाँचता है',
        body: 'रिटेलर WhatsApp पर ऑर्डर कन्फर्म करता है, फिर ऑर्डर स्टाफ की लिस्ट में आता है। हर लाइन पर Auto, Review या Ask लिखा होता है। स्टाफ ठीक करके अप्रूव करता है।',
      },
      {
        title: 'Tally में Sales Order',
        body: 'आपके PC पर चलने वाला छोटा कनेक्टर अप्रूव हुए ऑर्डर को Tally के रेट के साथ TallyPrime में Sales Order बनाता है। फिर रिटेलर को WhatsApp पर “आपका ऑर्डर बुक हो गया” जाता है।',
      },
    ],
  },
  pricing: {
    title: 'कीमत',
    intro: `${PILOT_DAYS} दिन का फ्री पायलट से शुरू करें। पैसे तभी दें जब स्टाफ का समय बचे।`,
    perMonth: '/महीना',
    ordersUpTo: (n) => `महीने में ${n.toLocaleString('en-IN')} ऑर्डर तक`,
    staffUpTo: (n) => `${n} स्टाफ लॉगिन`,
    retailersUpTo: (n) => `${n.toLocaleString('en-IN')} रिटेलर तक`,
    pilot: {
      name: 'फ्री पायलट',
      price: `${PILOT_DAYS} दिन फ्री`,
      points: [
        'आपका अपना WhatsApp और आपकी अपनी Tally',
        'हम आपके साथ सेटअप करते हैं और स्टाफ को सिखाते हैं',
        'न कार्ड, न कॉन्ट्रैक्ट। किसी भी दिन बंद करें',
      ],
    },
    startHere: 'यहीं से शुरू करें',
    planCta: 'फ्री पायलट से शुरुआत करें',
    plans: {
      starter: { name: 'Starter', points: ['1 Tally कंपनी'] },
      growth: { name: 'Growth', points: ['2 Tally कंपनी', 'जल्दी सपोर्ट'] },
      pro: { name: 'Pro', points: ['5 Tally कंपनी', 'उसी दिन सपोर्ट'] },
    },
    notes: [
      'प्लान से ज़्यादा ऑर्डर? महीने की सीमा पार होने पर भी ऑर्डर कभी नहीं रुकते; अगले प्लान पर जाने के लिए हम आपसे संपर्क करेंगे।',
      `एक बार का सेटअप ${rupees(SETUP_FEE)}, पायलट वाले डिस्ट्रीब्यूटर के लिए फ्री।`,
      'साल भर का एक साथ: 10 महीने के पैसे में 12 महीने।',
      'एक ऑर्डर यानी Tally में चढ़ा एक Sales Order; सवाल और रिजेक्ट हुए ऑर्डर नहीं गिने जाते।',
    ],
  },
  faq: {
    title: 'डिस्ट्रीब्यूटर के सवाल',
    intro: 'आपके रिटेलर, स्टाफ और Tally के लिए क्या बदलता है।',
    items: FAQ_BY_LANGUAGE.hi,
  },
  about: {
    title: 'Order Bot के बारे में',
    body: [
      'Order Bot आपके रिटेलर के भेजे WhatsApp ऑर्डर को आपकी Tally में Sales Order बना देता है, और हर ऑर्डर आपका स्टाफ अप्रूव करता है।',
      'आपका डेटा आपका है: हम उसे सिर्फ़ सर्विस चलाने के लिए रखते हैं, कभी बेचते या शेयर नहीं करते, और आपके कहने पर मिटा देते हैं।',
    ],
    builderTitle: 'कौन बनाता है',
    blurb:
      'फुल-स्टैक इंजीनियर (React, Node/NestJS), असली बिज़नेस के लिए AI वर्कफ़्लो बनाते हैं। Order Bot पूरा उन्होंने बनाया है: WhatsApp, AI, स्टाफ की लिस्ट और Tally कनेक्टर।',
  },
  hood: { summary: 'डेवलपर के लिए: Order Bot कैसे बना है (English में)' },
  closing: {
    title: 'Order Bot को अपने ही ऑर्डर पर आज़माएँ',
    body: `${PILOT_DAYS} दिन का फ्री पायलट, आपके WhatsApp नंबर और आपकी Tally के साथ। सेटअप हम आपके साथ करते हैं और स्टाफ को सिखाते हैं। न कार्ड, न कॉन्ट्रैक्ट।`,
    whatsapp: 'WhatsApp पर बात करें',
    whatsappText: 'नमस्ते, मुझे अपने डिस्ट्रीब्यूशन बिज़नेस के लिए Order Bot के बारे में जानना है।',
  },
  footer: {
    tagline: 'WhatsApp ऑर्डर सीधे TallyPrime में, हर ऑर्डर आपके स्टाफ की मंज़ूरी से।',
    productTitle: 'Order Bot',
    contactTitle: 'संपर्क',
    accountTitle: 'अकाउंट',
    demo: 'लाइव डेमो',
    email: 'ईमेल',
    whatsapp: 'WhatsApp',
    signup: 'अपना बिज़नेस साइन अप करें',
    staffLogin: 'स्टाफ लॉगिन',
    privacy: 'प्राइवेसी पॉलिसी',
    terms: 'शर्तें',
    developers: 'डेवलपर के लिए',
  },
};

export const LANDING_COPY: Record<LandingLang, LandingCopy> = { en, hi };

const STORAGE_KEY = 'orderbot.landing.lang';

export const readLandingLang = (): LandingLang => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'en' || saved === 'hi' ? saved : DEFAULT_LANDING_LANG;
  } catch {
    return DEFAULT_LANDING_LANG;
  }
};

export const saveLandingLang = (lang: LandingLang): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // private window or blocked storage: the choice just isn't remembered
  }
};
